import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { apiClient } from '../../lib/apiClient'
import type { ApiResponse } from '../../types'
import { getSlots } from '../appointments/appointment.api'
import type { ConsultationMode } from '../appointments/appointment.types'
import ErrorState from '../../components/common/ErrorState'
import { Skeleton } from '../../components/common/Loading'
import ContactForm from './ContactForm'
import DoctorDirectory from './DoctorDirectory'
interface Portfolio {
  doctor: {
    id: number
    name: string
    title: string
    qualifications: string
    registration_no: string
    experience_years: number
    bio: string
    chamber_name: string
    chamber_address: string
    phone: string
    photo_url?: string
    services: {
      id: number
      name: string
      department: string
      fee: string
      duration_minutes: number
    }[]
  }
  departments: { id: number; name: string; description: string }[]
  schedule: {
    id: number
    day_of_week: number
    start_time: string
    end_time: string
    consultation_mode: ConsultationMode
  }[]
  settings: Record<string, string>
  pages: { slug: string; title: string; content: string }[]
  faqs: { question: string; answer: string }[]
}
export default function PortfolioPage() {
  const location = useLocation()
  const q = useQuery({
    queryKey: ['portfolio'],
    queryFn: async () =>
      (await apiClient.get<ApiResponse<Portfolio>>('/public/portfolio')).data
        .data,
  })
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [service, setService] = useState('')
  const [mode, setMode] = useState<ConsultationMode>('chamber')
  const slots = useQuery({
    queryKey: ['public-slots', date, service, mode],
    queryFn: () => getSlots(q.data!.doctor.id, date, Number(service), mode),
    enabled: !!q.data && !!service,
  })
  useEffect(() => {
    if (!q.data) return
    const id = location.hash.slice(1) || ({ '/': 'home', '/appointment': 'doctors', '/about': 'about', '/services': 'services', '/schedule': 'schedule', '/contact': 'contact' } as Record<string, string>)[location.pathname]
    const target = id ? document.getElementById(id) : null
    if (!target) return
    const frame = requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'auto', block: 'start' })
      if (id !== 'home') target.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(frame)
  }, [q.data, location.pathname, location.hash, location.key])
  useEffect(() => {
    if (!q.data) return
    document.title =
      (q.data.settings.site_name || q.data.doctor.name) +
      ' | ' +
      (location.pathname === '/'
        ? 'Online consultation'
        : location.pathname.slice(1))
    for (const [name, content] of [
      ['description', q.data.doctor.bio],
      ['og:title', document.title],
      ['og:description', q.data.doctor.bio],
    ]) {
      let el = document.querySelector(
        'meta[' +
          (name.startsWith('og:') ? 'property' : 'name') +
          '="' +
          name +
          '"]',
      )
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    }
  }, [q.data, location.pathname])
  if (q.isLoading)
    return (
      <main className="public-page">
        <Skeleton lines={10} />
      </main>
    )
  if (q.isError || !q.data)
    return (
      <main className="public-page">
        <ErrorState title="Portfolio unavailable" onRetry={() => q.refetch()} />
      </main>
    )
  const p = q.data,
    d = p.doctor
  return (
    <main className="public-page">
      <section className="portfolio-foundation" id="home" tabIndex={-1}>
        <div className="portfolio-copy">
          <p className="eyebrow">{d.title}</p>
          <h1>{d.name}</h1>
          <p>{d.qualifications}</p>
          <p>
            Registration {d.registration_no} · {d.experience_years} years of
            experience
          </p>
          <p>{d.bio}</p>
          <div className="hero-actions">
            <Link className="btn btn-light" to="/appointment">
              Book appointment
            </Link>
            <Link className="btn btn-outline-light" to="/schedule">
              View schedule
            </Link>
          </div>
        </div>
        <div className="portfolio-visual">
          {d.photo_url ? (
            <img
              src={d.photo_url}
              alt={d.name}
              style={{ width: '100%', maxWidth: 320, borderRadius: 18 }}
            />
          ) : (
            <span aria-label="Doctor photograph not yet uploaded">
              <i className="bi bi-person-badge" />
            </span>
          )}
          <strong>{d.chamber_name}</strong>
          <small>{d.chamber_address}</small>
        </div>
      </section>
      <DoctorDirectory />
      <section className="panel mb-4" id="about" tabIndex={-1}>
        <div className="panel-body">
          <p className="eyebrow">About</p>
          <h2>{p.settings.about_title || p.settings.site_name || 'About us'}</h2>
          <p style={{ whiteSpace: 'pre-line' }}>{p.settings.about_content || d.bio}</p>
          <h2>Care for your everyday health</h2>
          <div className="stats-grid">
            {p.departments.map((x) => (
              <article className="stat-card" key={x.id}>
                <div>
                  <strong>{x.name}</strong>
                  <p>{x.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="panel mb-4" id="services" tabIndex={-1}>
        <div className="panel-body">
          <h2>Services & fees</h2>
          <div className="stats-grid">
            {d.services.map((s) => (
              <article className="stat-card" key={s.id}>
                <div>
                  <strong>{s.name}</strong>
                  <p>
                    {s.department} · {s.duration_minutes} minutes
                  </p>
                  <span>
                    {p.settings.currency || 'BDT'} {s.fee}
                  </span>
                  <Link
                    className="btn btn-sm btn-outline-primary mt-2"
                    to={`/appointment?service=${s.id}`}
                  >
                    Book this service
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <p className="mt-3">Chamber · Phone · Zoom · Google Meet</p>
        </div>
      </section>
      <section className="panel mb-4" id="schedule" tabIndex={-1}>
        <div className="panel-body">
          <h2>Weekly schedule & available slots</h2>
          {p.schedule.map((s) => (
            <p key={s.id}>
              {
                [
                  'Sunday',
                  'Monday',
                  'Tuesday',
                  'Wednesday',
                  'Thursday',
                  'Friday',
                  'Saturday',
                ][s.day_of_week]
              }
              : {s.start_time}–{s.end_time} ({s.consultation_mode})
            </p>
          ))}
          <div className="form-grid">
            <label>
              Service
              <select
                className="form-select"
                value={service}
                onChange={(e) => setService(e.target.value)}
              >
                <option value="">Select service</option>
                {d.services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Date
              <input
                className="form-control"
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
            <label>
              Mode
              <select
                className="form-select"
                value={mode}
                onChange={(e) => setMode(e.target.value as ConsultationMode)}
              >
                {['chamber', 'phone', 'zoom', 'google_meet'].map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </label>
          </div>
          {slots.isFetching ? (
            <Skeleton />
          ) : slots.isError ? (
            <ErrorState />
          ) : (
            <div className="slot-grid mt-3">
              {slots.data?.length
                ? slots.data.map((s) => (
                    <Link
                      className="slot-chip"
                      key={s.start_time}
                      to="/patient/appointments/new"
                    >
                      {s.start_time}
                    </Link>
                  ))
                : service && (
                    <p>No slots for this selection. Try another date.</p>
                  )}
            </div>
          )}
        </div>
      </section>
      <section className="panel mb-4 contact-section" id="contact" tabIndex={-1}>
        <div className="panel-body contact-layout">
          <aside className="contact-details">
          <p className="eyebrow">Let’s get in touch</p>
          <h2>Contact the chamber</h2>
          {Object.entries((()=>{try{return JSON.parse(p.settings.social_links||'{}') as Record<string,unknown>}catch{return {}}})()).filter(([,url])=>typeof url==='string'&&/^https:\/\//.test(url)).map(([label,url])=><a className="me-3" href={String(url)} key={label} target="_blank" rel="noreferrer">{label}</a>)}
          <p>{p.settings.chamber_address || d.chamber_address}</p>
          <p>
            {p.settings.contact_phone || d.phone} · {p.settings.contact_email}
          </p>
          <p>
            {p.settings.emergency_disclaimer ||
              'Online consultations are not an emergency service. Seek local emergency care when needed.'}
          </p>
          </aside>
          <ContactForm />
        </div>
      </section>
      <section className="panel mb-4">
        <div className="panel-body">
          <h2>Frequently asked questions</h2>
          {p.faqs.map((f) => (
            <details key={f.question}>
              <summary>{f.question}</summary>
              <p>{f.answer}</p>
            </details>
          ))}
        </div>
      </section>
      {p.pages.map((page) => (
        <section className="panel mb-3" key={page.slug} id={page.slug} tabIndex={-1}>
          <div className="panel-body">
            <h2>{page.title}</h2>
            <p style={{ whiteSpace: 'pre-line' }}>{page.content}</p>
          </div>
        </section>
      ))}
    </main>
  )
}
